<template>
  <v-card>
<v-card-title>
      {{ $t('lessonPlaner.title') }}
</v-card-title>
    <v-card-text>
    <v-container id="summary">
      <p v-if="numModules>0">
        {{ $t('lessonplanner.summary', [numModules, totalDuration]) }}
      </p>
      <p v-else>
        {{ $t('lessonplanner.empty') }}<br>
      </p>
    </v-container>
    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <spin v-if="loading">
      {{ $t('global.loading') }}
    </spin>
    <v-row>
      <v-col>
        <v-text-field v-if="!loading" v-model="search" :label="$t('global.search')" clearable />
      </v-col>
      <v-col>
        <v-switch v-model="hideAllLocales" :label="$t('lessonplanner.show-all-locales')" />
      </v-col>
  </v-row>
  </v-card-text>
    <v-container class="scrollable-container">
    <v-data-iterator
        v-if="!loading"
        :items="filteredModules"
        :items-per-page="-1"
        hide-default-footer
      >
        <template v-slot:default="props">
          <v-expansion-panels dense>
            <v-expansion-panel
              v-for="item in props.items"
              :key="item.title"
              dense
            >
              <v-expansion-panel-header dense>    
                  <v-checkbox v-model="selectionState[item.uid].selected" dense inset class="title-switch" :label="itemTitle(item)" @click.stop="handleModuleClick($event, item)" />
              </v-expansion-panel-header>
              <v-expansion-panel-content dense>
                <div class="margins">
                  <v-row>
                    <v-col cols="6">
                      <v-img
                        :src="item.image"
                      />
                    </v-col>
                    <v-col cols="6" class="description">
                      <p>
                        {{ itemDescription(item) }}
                      </p>
                      <p>
                        <a v-if="itemGuideLink(item)" :href="itemGuideLink(item)" target="_blank">{{ $t('lessonplanner.tutor-notes') }}</a>
                      </p>
                      <p>
                        <v-chip v-for="(language, index) in itemSupportedLanguages(item)" :key="index" class="mr-2">
{{ language }}
</v-chip><br>
                      </p>
                    </v-col>
                  </v-row>
                 
                  <div v-if="item.topics && item.navigationType === 'free'">
                    <div class="mt-3">
                    <em>{{ $t('lessonplanner.topics-header', [moduleDuration(item, true)]) }}</em>
                    </div>
                    
                    <v-row>
                      <v-col cols="6">
                        <v-checkbox 
                          v-for="(topic, i) in topicsSlice(item,0)"
                          :key="i"
                          v-model="selectionState[item.uid].topics[topic.id]"
                          :label="itemCheckpoint(item,topic)" 
                          class="slim"
                          :disabled="!selectionState[item.uid].selected"
                        />
                      </v-col>
                      <v-col cols="6">
                        <v-checkbox 
                          v-for="(topic, i) in topicsSlice(item,1)"
                          :key="i"
                          v-model="selectionState[item.uid].topics[topic.id]"
                          :label="itemCheckpoint(item,topic)"   
                          class="slim"
                          :disabled="!selectionState[item.uid].selected"
                        />
                      </v-col>
                    </v-row>
                  </div>
                   </div>
                   <div v-if="item.navigationType === 'free'" class="tip">
                      {{ $t('lessonPlanner.version-warning') }}
                    </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels> 
        </template>
      </v-data-iterator>
    </v-container>
    <v-card-actions>
      <v-spacer />
      <v-btn color="blue" @click="cancel">
{{ $t('global.cancel') }}
</v-btn>
      <v-btn color="primary" @click="save">
{{ $t('global.save') }}
</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { ContentLibrary } from '@/helpers/contentLibrary.js'
  
  export default {
    name: 'LessonPlanner',
    
    props: {
      value: {
        type: String,
        default: '',
      },
      moduleSelection: {
        type: [Object, Array],
        default: () => ({}),
      },
    },

    data () {
      return {
        search: '',
        modules: [], // loaded from API
        loading: false,
        selectionState: {},
        hideAllLocales: true,
      }
    },

    computed: {
      filteredModules () {
        const unsortedModules = this.modules

        const currentLocale = this.$i18n.locale ?? 'en'
        const filteredByLocale = this.hideAllLocales ? unsortedModules.filter(module => module.availableLocales.includes(currentLocale)) : unsortedModules

        const sortedModules = filteredByLocale.sort((a, b) => {
          const titleA = a.locales[this.$i18n.locale]?.title?.toLowerCase() ?? a.locales.en.title?.toLowerCase() ?? a.title ?? ''
          const titleB = b.locales[this.$i18n.locale]?.title?.toLowerCase() ?? b.locales.en.title?.toLowerCase() ?? b.title ?? ''
          return titleA.localeCompare(titleB, undefined, { sensitivity: 'accent' })
        })

        if (this.search === '' || this.search === null) return sortedModules

        const searchTerm = this.search.toLowerCase()
        const filteredModules = sortedModules.filter(module => 
          (module.locales[this.$i18n.locale].keywords && module.locales[this.$i18n.locale].keywords.some(keyword => keyword.toLowerCase() === searchTerm)) ||
          module.locales[this.$i18n.locale].title.toLowerCase().includes(searchTerm))
        
        return filteredModules
      },
      
      totalDuration () {
        // sum up the duration of all selected topics for each module
        let total = 0
        this.modules.forEach((module) => {
          total += this.moduleDuration(module)
        })
        return total
      },
      numModules () {
        // count the number of selected modules
        let total = 0
        this.modules.forEach((module) => {
          if (this.selectionState[module.uid].selected) {
            total += 1
          }
        })
        // if (total === 0) return 'no'
        return total
      },
      contentString () {
        return this.contentArray.join(',')
      },
      contentArray () {
        // console.log('selectionState = ' + JSON.stringify(this.selectionState))
        const contentArray = this.modules
          .filter((module) => this.selectionState[module.uid].selected)
          .map((module) => {
            let topicsString = ''
            if (module.topics) {
              topicsString = module.topics
                .filter((topic) => this.selectionState[module.uid].topics[topic.id])
                .map((topic) => topic.id)
                .join(',')
              if (topicsString !== '') topicsString = '(' + topicsString + ')'
            }
            // console.log(topicsString)
            return module.uid + topicsString
          })
        // console.log('contentArray = ' + JSON.stringify(contentArray))
        return contentArray
      },
    },

    watch: {
      moduleSelection (newValue) {
        // console.log('moduleSelection changed + ' + JSON.stringify(newValue))
        if (newValue) {
          this.initSelection()
        }
      },
    },

    created () {
      this.initSelection()
    },

    async mounted () {
      this.loading = true
      // this.modules = this.$store.state.contentLibrary
      const contents = await ContentLibrary.Get(this.$store, this.$i18n, [ContentLibrary.ContentType.MODULE, ContentLibrary.ContentType.SIMULATOR])
      // need to remove content that is not visible to this user, this is done by filtering by the purchased field
      const modules = contents === null ? [] : contents.filter((x) => { 
        return x.purchased && [ContentLibrary.ContentType.MODULE, ContentLibrary.ContentType.SIMULATOR].includes(x.type) 
      })
      // go through modules and insert a field called image that is the name of the module
      modules.forEach((module) => {
        module.image = 'https://storage.googleapis.com/bodyswaps-public/img/' + module.uid + '.jpg'
      })
      this.modules = modules
      this.initSelection()
      this.loading = false
    },

    methods: {
      topicsSlice (item, col) {
        const topics = item.topics.filter((topic) => topic.id !== 'complete')
        if (col === 0) {
          return topics.slice(0, Math.ceil(topics.length / 2))
        } else {
          return topics.slice(Math.ceil(topics.length / 2))
        }
      },
      moduleDuration (module, ignoreSelection = false) {
        let total = 0
        if (this.selectionState[module.uid].selected || ignoreSelection) {
          if (module.navigationType !== 'free') 
          {
            total += module.duration 
          }
          else
          {
            if (module.topics) {
              module.topics.forEach((topic) => {
                if (this.selectionState[module.uid].topics[topic.id]) {
                  total += topic.duration
                }
              })
            }
          }
        }
        return total
      },
      handleModuleClick (event, item) {
        event.stopPropagation()
      },
      itemTitle (item) {
        const currentLocale = this.$i18n.locale ?? 'en'
        return item.locales?.[currentLocale]?.title ?? item.locales?.en.title ?? item.uid ?? item.id
      },
      itemDescription (item) {
        const currentLocale = this.$i18n.locale ?? 'en'
        return item.locales?.[currentLocale]?.promoText ?? this.$t('lessonplanner.no-description')
      },
      itemCheckpoint (item, topic) {
        const currentLocale = this.$i18n.locale ?? 'en'
        return item.locales?.[currentLocale]?.topicNames?.[topic.id] ?? topic.id
      },
      itemGuideLink (item) {
        return item.infoLink ?? null
      },
      itemSupportedLanguages (item) {
        // if item.locales?[locale] has captions then add the string "-cc" to the locale in locales
        return Object.keys(item.locales).map(locale => {
          if (item.locales?.[locale].captioned) {
            return locale + ' (captions)'
          } else {
            return locale
          }
        })
      },
      itemCaptions (item) {
        const currentLocale = this.$i18n.locale ?? 'en'
        return item.locales?.[currentLocale]?.captions ?? false
      },
      save () {
        console.log(JSON.stringify(this.contentArray))
        this.$emit('save', this.contentArray)
      },
      cancel () {
        this.$emit('cancel')
      },
      initSelection () {
        this.modules.forEach(module => {
          this.$set(this.selectionState, module.uid, { selected: false, topics: {} })
          if (module.topics) {
            module.topics.forEach(topic => {
              this.$set(this.selectionState[module.uid].topics, topic.id, true)
            })
          }
        })
        // if there is a moduleSelection prop, use it to set the selection state
        if (this.moduleSelection) {
          // console.log('setting state ' + JSON.stringify(this.moduleSelection))
          this.moduleSelection.forEach((moduleSelection) => {
            const justModuleID = moduleSelection.split('(')[0]
            const module = this.modules.find((module) => module.uid === justModuleID)
            if (module) {
              const selectedTopics = moduleSelection.split('(')[1] ? moduleSelection.split('(')[1].split(')')[0].split(',') : []
              // take each selectedTopics and create a dictionary with the topic id as the key and true as the value
              const selectedTopicsMap = {}
              selectedTopics.forEach((selectedTopic) => {
                selectedTopicsMap[selectedTopic] = true
              })

              // console.log('selectedTopicsMap = ' + JSON.stringify(selectedTopicsMap))
              this.$set(this.selectionState, module.uid, { selected: true, topics: selectedTopicsMap ?? {} })
            }
          })
        }
        // console.log('new selectionState = ' + JSON.stringify(this.selectionState))
      },
    },
  }
</script>

<style scoped>
.columns {
  column-count: 2;
  column-gap: 2em; /* add some space between columns */
}

.item {
  break-inside: avoid; /* prevent column breaks inside items */
  margin-bottom: 1em; /* add some space at the bottom of each item */
}

.slim {
  height: 14px;
  margin-top: 0px;
  margin-bottom: 10px;
}

.margins {
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 16px;
}

.scrollable-container {
  height: 480px; /* Define the height you need */
  overflow-y: auto;
}

#summary {
  padding-top: 16px;
  width: 100%;
  font-size: larger;
  background-color: lightgray;
}

#summary p {
  margin-bottom: 0px;
}

.custom-card-title {
  height: 40px; /* Override the height */
  color: black; /* Override the text color */
  background-color: white;
}

.custom-card-title-text {
  color: white; /* Override the text color */
  font-size: 16px;
  height: 32px;
}


.description {
  line-height: 1.2em;
}

.modulecard {
  margin-top: 0px;
  margin-bottom: 0px;
}

.title-switch {
  margin-top: 0px;
  margin-bottom: 0px; 
}

.title-switch .v-label {
  color: white !important;
}

.tip {
  font-size: 12px;
  color: gray;
}
</style>
